import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import { useSelectionListKeyHandler } from "src/hooks/ticket/selection-list-key-handler/useSelectionListKeyHandler";
import { boldenMatchedSubstring } from "src/utils/Common";
import { IVatData } from "src/stores/krediflow/InvoiceTypes";
import { ErpType } from "src/network/User";
import { LOADING_VAT_DATA_STATE } from "src/stores/krediflow/AccountingVatStore";
import styles from "./VatRateField.module.css";

interface IVatRateFieldProps {
    accountingRowIndex: number;
}

const VatRateFieldBase = (props: IVatRateFieldProps) => {
    const { t } = useTranslation();
    const { accountingVatStore, invoiceStore, authStore } = useContext(RootStoreContext);

    const { accountingRowIndex } = props;

    const [displayVatRateList, setDisplayVatRateList] = useState<boolean>(false);
    const [lastScrollPositionYVatRateItems, setLastSrollYPositionVatRateItems] = useState<number>(0);

    const vatRateInputRef = useRef<HTMLInputElement>(null);

    const updateAccountingWithSelectedVatRate = useCallback(
        async (vatData: IVatData) => {
            invoiceStore.updateAccountingWithSelectedVatRate(vatData, accountingRowIndex);
        },
        [invoiceStore, accountingRowIndex]
    );

    const resetSearchQuery = useCallback(() => {
        const currentlySelectedVatRateQueryString =
            accountingVatStore.getSelectedVatRateQueryString(accountingRowIndex);

        const currentVatRateSearchQuery = accountingVatStore.getCurrentVatRateSearchQuery(accountingRowIndex);

        if (accountingVatStore.isValidVatRateForSelectedVatCode(accountingRowIndex, currentVatRateSearchQuery)) {
            const vatData = accountingVatStore.getVatDataByVatRate(accountingRowIndex, currentVatRateSearchQuery);

            if (vatData) {
                updateAccountingWithSelectedVatRate(vatData);
            }
        } else {
            accountingVatStore.setCurrentVatRateSearchQuery(
                currentlySelectedVatRateQueryString ?? "",
                accountingRowIndex
            );
        }
    }, [accountingRowIndex, accountingVatStore, updateAccountingWithSelectedVatRate]);

    useEffect(() => {
        const handleClickOutsideVatRateInput = (event: any) => {
            const target = event.target as HTMLElement;

            const isClickOutsideVatRateInputOrList = () => {
                if (!displayVatRateList || !vatRateInputRef.current) {
                    return true;
                }

                const clickedInsideInput = vatRateInputRef.current.contains(target);

                const clickedListItem =
                    target.classList.contains("list-item") && target.classList.contains("accounting-vatrate");

                const clickedIcon = target.localName === "path" || target.localName === "svg";

                const clickedHighlightedText =
                    target.classList.contains("highlighted-text") && target.classList.contains("accounting-vatrate");

                const clickedListItemChildElement =
                    target.parentElement?.classList.contains("list-item") &&
                    target.parentElement.classList.contains("accounting-vatrate");

                return !(
                    clickedInsideInput ||
                    clickedListItem ||
                    clickedListItemChildElement ||
                    clickedIcon ||
                    clickedHighlightedText
                );
            };

            if (isClickOutsideVatRateInputOrList()) {
                setDisplayVatRateList(false);

                if (
                    invoiceStore.currentInvoice?.isAccountingVatRateSelected(accountingRowIndex) &&
                    accountingVatStore.getCurrentVatRateSearchQuery(accountingRowIndex) !==
                        accountingVatStore.getSelectedVatRateQueryString(accountingRowIndex)
                ) {
                    resetSearchQuery();
                }
            }
        };

        document.addEventListener("mousedown", handleClickOutsideVatRateInput);

        return () => {
            document.removeEventListener("mousedown", handleClickOutsideVatRateInput);
        };
    }, [
        displayVatRateList,
        resetSearchQuery,
        accountingVatStore,
        invoiceStore.currentInvoice?.isAccountingVatRateSelected,
        accountingRowIndex,
        invoiceStore
    ]);

    const onSelectVatRateItem = useCallback(
        (activeListItemIndex: number) => {
            const selectedVatDataItem =
                accountingVatStore.selectableVatRateData(accountingRowIndex)[activeListItemIndex];

            updateAccountingWithSelectedVatRate(selectedVatDataItem);
        },
        [updateAccountingWithSelectedVatRate, accountingVatStore, accountingRowIndex]
    );

    const onTabFromVatRateField = useCallback(() => {
        if (accountingVatStore.selectableVatRateData(accountingRowIndex).length === 1) {
            const onlyVatRateItemInList = accountingVatStore.selectableVatRateData(accountingRowIndex)[0];
            updateAccountingWithSelectedVatRate(onlyVatRateItemInList);
        }

        accountingVatStore.setTriggerFocusOnNextAccountingField(true, accountingRowIndex);
    }, [accountingVatStore, accountingRowIndex, updateAccountingWithSelectedVatRate]);

    const { onKeyDownFunction, setActiveListItemIndex, NO_LIST_ITEM_SELECTED } = useSelectionListKeyHandler({
        listName: "accounting-vatrate",
        totalNumberOfDisplayedItems: invoiceStore.numberOfDisplayedItems,
        setIsListDisplayed: setDisplayVatRateList,
        isListDisplayed: displayVatRateList,
        onSelectItem: onSelectVatRateItem,
        resetSearchQuery: resetSearchQuery,
        additionalTabActionWithNoItemSelected: onTabFromVatRateField
    });

    const resetAmountOfItemsDisplayedInVatRateList = useCallback(() => {
        setLastSrollYPositionVatRateItems(0);
        invoiceStore.setNumberOfDisplayedItems(invoiceStore.sliceSizeOfDisplayedItems);
    }, [invoiceStore]);

    const onChangeVatRateInput = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            resetAmountOfItemsDisplayedInVatRateList();

            const inputAccount = e.target.value;
            accountingVatStore.setCurrentVatRateSearchQuery(inputAccount, accountingRowIndex);
        },
        [accountingVatStore, resetAmountOfItemsDisplayedInVatRateList, accountingRowIndex]
    );

    const onFocusVatRateInput = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            if (
                invoiceStore.currentInvoice?.isAccountingVatRateSelected(accountingRowIndex) &&
                vatRateInputRef.current
            ) {
                vatRateInputRef.current.select();
            }

            resetAmountOfItemsDisplayedInVatRateList();
            setDisplayVatRateList(true);
            setActiveListItemIndex(NO_LIST_ITEM_SELECTED);
        },
        [
            resetAmountOfItemsDisplayedInVatRateList,
            setActiveListItemIndex,
            NO_LIST_ITEM_SELECTED,
            invoiceStore.currentInvoice,
            accountingRowIndex
        ]
    );

    const onClickVatRateItem = useCallback(
        (vatData: IVatData) => async (e: any) => {
            setActiveListItemIndex(NO_LIST_ITEM_SELECTED);
            setDisplayVatRateList(false);

            updateAccountingWithSelectedVatRate(vatData);
        },
        [setActiveListItemIndex, NO_LIST_ITEM_SELECTED, updateAccountingWithSelectedVatRate]
    );

    const onScrollVatRateList = useCallback(
        (e: React.BaseSyntheticEvent) => {
            const windowHeight = e.target.clientHeight;
            const pixelsFromTop = e.target.scrollTop;
            const totalHeight = e.target.scrollHeight;

            const is150PixelsFromBottom = totalHeight - pixelsFromTop < windowHeight + 150;
            const isScrollingDown = pixelsFromTop > lastScrollPositionYVatRateItems;

            const hasMoreResulsToDisplay =
                invoiceStore.numberOfDisplayedItems <
                accountingVatStore.selectableVatRateData(accountingRowIndex).length;

            if (is150PixelsFromBottom && isScrollingDown && hasMoreResulsToDisplay) {
                invoiceStore.setNumberOfDisplayedItems(
                    invoiceStore.numberOfDisplayedItems + invoiceStore.sliceSizeOfDisplayedItems
                );
            }

            setLastSrollYPositionVatRateItems(pixelsFromTop);
        },
        [lastScrollPositionYVatRateItems, invoiceStore, accountingVatStore, accountingRowIndex]
    );

    const vatRateList = (
        <ul
            id="accounting-vatrate-dropdown-list"
            className={`dropdown-list accounting-vatrate krediflow ${
                authStore.user?.erpType === ErpType.IT2 ? "IT2" : "RIMO"
            }`}
            onScroll={onScrollVatRateList}
        >
            {accountingVatStore.loadingVatDataState === LOADING_VAT_DATA_STATE.LOADING ? (
                <li className="d-flex align-items-center justify-content-center list-item accounting-vatrate loading">
                    <div>
                        <UI.RotatingSpinner noLogo size={20} />
                    </div>
                </li>
            ) : (
                accountingVatStore
                    .selectableVatRateData(accountingRowIndex)
                    .slice(0, invoiceStore.numberOfDisplayedItems)
                    .map((vatData, index) => {
                        const vatRateName = accountingVatStore.getFormattedVatRateFieldValue(vatData);

                        const currentVatRateSearchQuery =
                            accountingVatStore.getCurrentVatRateSearchQuery(accountingRowIndex);

                        const formattedVatRateName = boldenMatchedSubstring(
                            currentVatRateSearchQuery ? currentVatRateSearchQuery.toString() : "",
                            vatRateName,
                            "highlighted-text accounting-vatrate"
                        );

                        return (
                            <li
                                key={index}
                                className="d-flex align-items-center list-item accounting-vatrate"
                                onClick={onClickVatRateItem(vatData)}
                                id={`accounting-vatrate-list-item-${index}`}
                            >
                                <div
                                    className={`accounting-vatrate-text ${styles.AccountingAccountText}`}
                                    dangerouslySetInnerHTML={{
                                        __html: formattedVatRateName
                                    }}
                                />
                            </li>
                        );
                    })
            )}
        </ul>
    );

    return (
        <>
            <UI.Input
                ref={vatRateInputRef}
                autoComplete="off"
                label={t("screens.kredi_flow.accountings.vat_rate").toString()}
                type="text"
                value={accountingVatStore.getCurrentVatRateSearchQuery(accountingRowIndex) ?? ""}
                onChange={onChangeVatRateInput}
                onFocus={onFocusVatRateInput}
                onKeyDown={onKeyDownFunction}
                disabled={!invoiceStore.currentInvoice?.canEditAccountingVatRateField(accountingRowIndex)}
            />
            {displayVatRateList ? vatRateList : undefined}
        </>
    );
};

export const VatRateField = Sentry.withProfiler(observer(VatRateFieldBase));
